import type { PopoverPassThroughTransitionType } from 'primevue/popover';
import type { Ref } from 'vue';

export type DropdownInstance = {
  /**
   * The element that contains the dropdown content.
   */
  container?: HTMLElement;
  /**
   * The element that triggers the dropdown.
   */
  target?: HTMLElement;
  unbindContentResizeListener?: () => void;
};

export function getAlignDropdownRightUtils({
  dropdownRef,
}: {
  /**
   * PrimeVue does not have a concept of a "dropdown". Instead, two types of components map to this concept:
   * - Popover, or
   * - Menu
   *
   * These utils may be used with either type of component, so we refer to the component as a "dropdown" for brevity.
   */
  dropdownRef: Ref<DropdownInstance | null>;
}): {
  /**
   * PrimeVue's default overlay enter animation scales from 0.93. That makes
   * dropdown content (e.g. switches) appear to shift vertically after being shown, and it also shrinks an
   * end-aligned menu off the button's right edge until the animation finishes
   */
  ptOverride: { transition: PopoverPassThroughTransitionType };
  alignMenuWithEnd: (options?: { shouldDropdownMatchTriggerWidth?: boolean }) => void;
} {
  /**
   * PrimeVue's absolutePosition writes insetInlineStart, so overwrite it to align the menu with the end of the trigger button.
   * Callers paint the visible panel on the positioned overlay, so overlay.offsetWidth is the width to align.
   */
  const setDropdownInlineStart = ({
    target,
    dropdown,
    shouldDropdownMatchTriggerWidth,
  }: {
    target: HTMLElement;
    dropdown: HTMLElement;
    shouldDropdownMatchTriggerWidth?: boolean;
  }): void => {
    if (shouldDropdownMatchTriggerWidth) {
      // PrimeVue only raises min-width to the trigger when the overlay is already narrower.
      // Bootstrap `.dropdown-menu` min-width can make the panel wider than a block button; match the trigger.
      dropdown.style.minWidth = `${target.offsetWidth}px`;
    }

    const inlineStart = target.getBoundingClientRect().right - dropdown.offsetWidth + window.scrollX;

    dropdown.style.left = '';
    dropdown.style.insetInlineEnd = '';
    dropdown.style.insetInlineStart = `${inlineStart}px`;
  };

  const alignMenuWithEnd = ({
    shouldDropdownMatchTriggerWidth,
  }: {
    shouldDropdownMatchTriggerWidth?: boolean;
  } = {}): void => {
    if (!dropdownRef.value) return;

    const { container, target } = dropdownRef.value;
    if (container == null || target == null) return;

    const dropdownContext = { target, dropdown: container, shouldDropdownMatchTriggerWidth };

    setDropdownInlineStart(dropdownContext);

    // PrimeVue's ResizeObserver re-runs start-alignment unless we disconnect it
    dropdownRef.value?.unbindContentResizeListener?.();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDropdownInlineStart(dropdownContext);
      });
    });
  };

  const ptOverride: { transition: PopoverPassThroughTransitionType } = {
    transition: {
      css: false,
    },
  };

  return {
    ptOverride,
    alignMenuWithEnd,
  };
}
